import { useState, useEffect, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import type { Provider } from './ProvidersPage';

// ─── Styles (matching RecordVitalsDrawer / EditPatientDetailsDrawer) ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text)',
  display: 'block',
  marginBottom: 'var(--sp-1)',
};

const inputBaseStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

// ─── Types Chip Selector ───

const ALL_TYPES: string[] = ['Doctor', 'Therapist', 'Consultant'];

function TypesChipSelect({
  value,
  onChange,
}: {
  value: string[];
  onChange: (types: string[]) => void;
}) {
  const toggle = (type: string) => {
    if (value.includes(type)) {
      onChange(value.filter((t) => t !== type));
    } else {
      onChange([...value, type]);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
      {ALL_TYPES.map((type) => {
        const selected = value.includes(type);
        return (
          <button
            key={type}
            type="button"
            onClick={() => toggle(type)}
            style={{
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x)',
              borderRadius: 'var(--input-radius)',
              background: selected ? 'var(--color-primary)' : 'var(--input-bg)',
              color: selected ? 'var(--text-on-primary, #fff)' : 'var(--input-text)',
              border: selected ? '1px solid var(--color-primary)' : '1px solid var(--border-input)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
          >
            {type}
          </button>
        );
      })}
    </div>
  );
}

// ─── Active Toggle (matching RecordVitalsDrawer pattern) ───

function ActiveToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Default form state ───

function defaultForm(provider?: Provider | null): FormState {
  if (provider) {
    return {
      fullName: provider.fullName,
      displayName: provider.displayName,
      specialty: provider.specialty,
      types: [...provider.types],
      email: provider.email,
      mobile: provider.mobile,
      active: provider.active,
    };
  }
  return {
    fullName: '',
    displayName: '',
    specialty: '',
    types: [],
    email: '',
    mobile: '',
    active: true,
  };
}

interface FormState {
  fullName: string;
  displayName: string;
  specialty: string;
  types: string[];
  email: string;
  mobile: string;
  active: boolean;
}

// ─── Appointment Picker Preview ───

function AppointmentPickerPreview({ name }: { name: string }) {
  const displayName = name.trim() || 'Provider Name';
  const initials = displayName
    .replace(/^Dr\.\s*/i, '')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        paddingTop: 'var(--sp-4)',
        marginTop: 'var(--sp-2)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Appointment Picker Preview
      </span>
      <div
        style={{
          marginTop: 'var(--sp-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-3)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            flexShrink: 0,
          }}
        >
          {initials || 'PN'}
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {displayName}
        </span>
      </div>
    </div>
  );
}

// ─── Provider Drawer ───

interface ProviderDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  provider: Provider | null;
  onSave: (data: {
    fullName: string;
    displayName: string;
    specialty: string;
    types: string[];
    email: string;
    mobile: string;
    active: boolean;
    avatarColor: string;
  }) => void;
}

export function ProviderDrawer({
  open,
  onOpenChange,
  brandTokens,
  provider,
  onSave,
}: ProviderDrawerProps) {
  const [form, setForm] = useState<FormState>(defaultForm(provider));

  // Reset form when drawer opens or provider changes
  useEffect(() => {
    if (open) {
      setForm(defaultForm(provider));
    }
  }, [open, provider]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const AVATAR_COLORS = ['#e67e22', '#27ae60', '#8e44ad', '#2980b9', '#c0392b', '#16a085'];
    onSave({
      fullName: form.fullName,
      displayName: form.displayName,
      specialty: form.specialty,
      types: form.types,
      email: form.email,
      mobile: form.mobile,
      active: form.active,
      avatarColor:
        provider?.avatarColor ?? AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
    });
  };

  const isEdit = !!provider;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit Provider' : 'New Provider'}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="lg"
            onClick={handleSave}
            disabled={!form.fullName.trim()}
          >
            Save Provider
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Full Name */}
        <div>
          <label style={labelStyle}>
            Full Name<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="text"
            placeholder="Dr. Sarah Chen"
            value={form.fullName}
            onChange={(e) => update('fullName', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Display Name */}
        <div>
          <label style={labelStyle}>Display Name</label>
          <input
            type="text"
            placeholder="Dr. Chen"
            value={form.displayName}
            onChange={(e) => update('displayName', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Specialty */}
        <div>
          <label style={labelStyle}>Specialty</label>
          <input
            type="text"
            placeholder="Cardiologist"
            value={form.specialty}
            onChange={(e) => update('specialty', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Types */}
        <div>
          <label style={labelStyle}>
            Types<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <TypesChipSelect value={form.types} onChange={(types) => update('types', types)} />
        </div>

        {/* Email */}
        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            placeholder="email@heliosmedgroup.com"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Mobile */}
        <div>
          <label style={labelStyle}>Mobile</label>
          <input
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={form.mobile}
            onChange={(e) => update('mobile', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Active Toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
          <ActiveToggle checked={form.active} onChange={(val) => update('active', val)} />
        </div>

        {/* Profile Photo (disabled) */}
        <div>
          <label style={labelStyle}>Profile Photo</label>
          <div
            style={{
              height: 'var(--input-height)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg-readonly)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--input-text-readonly)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Coming soon
          </div>
        </div>

        {/* Appointment Picker Preview */}
        <AppointmentPickerPreview name={form.displayName || form.fullName} />
      </div>
    </Drawer>
  );
}
